<script setup lang="ts">
interface TimelineItem {
  time: string;
  title: string;
  description: string;
}

withDefaults(
  defineProps<{
    heading?: string;
    subcopy?: string;
    items?: TimelineItem[];
  }>(),
  {
    heading: "After You Hit Contact",
    subcopy: "Here's exactly what happens next.",
    items: () => [
      {
        time: "Day 1",
        title: "Intro Call",
        description:
          "Thirty minutes, no pitch deck. We listen, ask about your goals and your data, and tell you honestly if we're the right fit.",
      },
      {
        time: "Day 2–3",
        title: "Proposal & Quote",
        description:
          "A scoped plan: architecture, timeline, milestones and a fixed price. No vague line items to decode later.",
      },
      {
        time: "Week 1",
        title: "Kickoff",
        description:
          "A dedicated Project Manager, a shared roadmap and environments set up. You get full visibility from day one.",
      },
      {
        time: "Ongoing",
        title: "Sprint Delivery",
        description:
          "Weekly demos, milestone reports and a channel where a question gets a same-day answer.",
      },
    ],
  },
);
</script>

<template>
  <section class="relative">
    <span
      class="hidden dark:block size-[463px] bg-[#47116B] rounded-full blur-[150px] absolute -left-72 top-24"
    />
    <div class="flex flex-col justify-center text-center">
      <h2>
        <WrGradientText :text="heading" />
        <span> No Mystery, No Runaround</span>
      </h2>
      <p class="text-lg mt-1.5 tracking-[0.5px]">{{ subcopy }}</p>
    </div>

    <div class="relative max-w-4xl mx-auto lg:mt-14 mt-8">
      <span
        aria-hidden="true"
        class="absolute top-1 bottom-1 left-4 lg:left-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-[#12DBFF] via-[#BA55FE] to-transparent"
      />
      <ol class="relative">
      <li
        v-for="(item, index) in items"
        :key="item.title"
        class="relative pl-12 pb-10 last:pb-0 lg:w-1/2"
        :class="
          index % 2 === 0
            ? 'lg:pl-0 lg:pr-12 lg:text-right'
            : 'lg:ml-auto lg:pl-12'
        "
      >
        <Reveal :delay="index * 100">
          <p
            class="font-avant-demi text-sm uppercase tracking-[2px] text-transparent bg-[linear-gradient(92deg,#12DBFF_-3.57%,#BA55FE_80.72%)] bg-clip-text"
          >
            {{ item.time }}
          </p>
          <h3 class="mt-1.5 text-lg font-semibold">{{ item.title }}</h3>
          <p class="mt-2 text-sm tracking-[0.5px] text-left">{{ item.description }}</p>
        </Reveal>
        <span
          class="absolute top-1 left-4 -translate-x-1/2"
          :class="
            index % 2 === 0
              ? 'lg:left-auto lg:right-0 lg:translate-x-1/2'
              : 'lg:left-0 lg:-translate-x-1/2'
          "
        >
          <span class="relative block size-3.5">
            <span
              v-for="layer in 3"
              :key="layer"
              class="dot-ring absolute inset-0 rounded-full bg-[#BA55FE]"
              :style="{ animationDelay: `${(layer - 1) * 1.3 + index * 0.2}s` }"
            />
            <span class="timeline-dot relative block size-full rounded-full bg-[#BA55FE]" />
          </span>
        </span>
      </li>
      </ol>
    </div>
  </section>
</template>

<style scoped>
.dot-ring {
  opacity: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .timeline-dot {
    animation: dot-pulse 4s ease-in-out infinite;
  }

  .dot-ring {
    animation: dot-ring-expand 4s ease-out infinite;
  }
}

@keyframes dot-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

@keyframes dot-ring-expand {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  100% {
    transform: scale(3.5);
    opacity: 0;
  }
}
</style>
